import React, { useEffect, useState } from "react";
import { Box, Typography, Container, Button, Grid, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { alpha } from "@mui/material/styles";
import {
  FaUserPlus,
  FaSlidersH,
  FaSearch,
  FaHandshake,
  FaShieldAlt,
  FaBalanceScale,
  FaMapMarkedAlt,
  FaBell,
  FaRocket,
  FaSyncAlt,
  FaChartLine,
  FaClock,
  FaUserShield, // Verified sellers
  FaTags, // Competitive pricing
  FaTruck, // Logistics/delivery
} from "react-icons/fa";
import RandomProductHomeSection from "./buyer/RandomProductHomeSection";
import Seo from "../components/Seo";

import Breadcrumb from "../components/Breadcrumb";
import { generateBreadcrumbs } from "../utils/breadcrumbs";
import KycBanner from "../components/common/KycBanner";

import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TrendingUp, Factory, Globe, Star } from "lucide-react";
import CountUp from "react-countup";
import { logoutUser, setCredentials } from "../features/auth/authSlice";
import VerifySwitchRoleModal from "../components/buyer/VerifySwitchRoleModal";
import { switchRole } from "../features/user/userSlice";
import Loader from "../components/common/Loader";

const themeColors = {
  primary: "#F5B800",
  secondary: "#334155",
  background: "#f8fafd",
  cardBackground: "#ffffff",
  shadow: "rgba(0, 0, 0, 0.08)",
  ctaBackground: "#2d3748",
};

const BuyerLandingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6 },
    }),
  };

  const benefits = [
    {
      title: "Verified Sellers",
      desc: "Source from trusted suppliers with complete verification.",
      icon: <FaShieldAlt size={28} color={themeColors.primary} />,
    },
    {
      title: "Competitive Pricing",
      desc: "Save more with bids, offers, and reverse bidding.",
      icon: <FaBalanceScale size={28} color={themeColors.primary} />,
    },
    {
      title: "Secure Payments",
      desc: "Escrow protection guarantees safe transactions.",
      icon: <FaMapMarkedAlt size={28} color={themeColors.primary} />,
    },
    {
      title: "Logistics Support",
      desc: "Simplify delivery with integrated pickup options.",
      icon: <FaBell size={28} color={themeColors.primary} />,
    },
  ];

  const journeyIcons = [FaUserPlus, FaSlidersH, FaSearch, FaHandshake];

  const extraBenefits = [
    {
      title: "Verified Sellers",
      desc: "Source only from trusted, verified suppliers for peace of mind.",
      icon: <FaRocket size={28} color={themeColors.primary} />,
    },
    {
      title: "Competitive Pricing",
      desc: "Save more with bids, offers, and reverse bidding options.",
      icon: <FaSyncAlt size={28} color={themeColors.primary} />,
    },
    {
      title: "Secure Transactions",
      desc: "Every payment is escrow-protected to guarantee safety.",
      icon: <FaChartLine size={28} color={themeColors.primary} />,
    },
    {
      title: "Seamless Logistics",
      desc: "Integrated tools make delivery and pickup simple and reliable.",
      icon: <FaClock size={28} color={themeColors.primary} />,
    },
  ];

  const breadcrumbs = generateBreadcrumbs({ pageType: "buy" });

  const { token, role, roles, user } = useSelector((state) => state.auth);
  const [isSwitching, setIsSwitching] = useState(false);
  const isLoggedIn = Boolean(token);
  const isBuyerLoggedIn = Boolean(token && role === "buyer");

  const [productsReady, setProductsReady] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  //  Prerender effect
  useEffect(() => {
    window.prerenderReady = false;
    // console.log("BuyerLandingPage: prerenderReady = false");

    if (productsReady) {
      setTimeout(() => {
        window.prerenderReady = true;
        // console.log("BuyerLandingPage: prerenderReady = true");
      }, 500); // slight delay for DOM updates
    }
  }, [productsReady]);

  // Call this function when your RandomProductHomeSection finishes loading products
  const handleProductsReady = () => {
    setProductsReady(true);
  };

  // 1. Does the actual API call + credentials update
  const handleRoleSwitch = async (targetRole) => {
    setIsSwitching(true);

    try {
      const result = await dispatch(switchRole({ role: targetRole })).unwrap();

      // No fetchUserAddresses needed for buyer
      const freshUser = result?.data?.user || result?.user;
      const updatedRoles = roles?.includes(targetRole)
        ? roles
        : [...(roles || []), targetRole];
      const mergedUser = {
        ...user,
        ...(freshUser || {}),
        roles: updatedRoles,
      };

      dispatch(
        setCredentials({
          user: mergedUser,
          role: targetRole,
          roles: updatedRoles,
        }),
      );

      // Navigate to buyer dashboard
      if (targetRole === "buyer") {
        navigate("/dashboard");
      } else {
        navigate("/seller/dashboard");
      }
    } catch (error) {
      console.error("Role switch failed:", error);
    } finally {
      setIsSwitching(false);
    }
  };

  // 2. Called by modal's onConfirm
  const handleConfirmRoleSwitch = () => {
    handleRoleSwitch("buyer");
  };

  // 3. Called by button click — guard logic only
  const handleBuyerRoleSwitch = async () => {
    console.log("🔍 Debug Info:", { isLoggedIn, role, roles });
    console.log("🔍 Roles array content:", roles);
    console.log("🔍 First role:", roles?.[0]);

    if (!isLoggedIn) {
      navigate("/login", { state: { role: "buyer" } });
      return;
    }

    // Already a buyer → go to dashboard
    if (role === "buyer" || roles?.includes("buyer")) {
      console.log("✅ Navigating to buyer dashboard");
      navigate("/dashboard");
      return;
    }

    // Logged in but not registered as buyer → show modal
    if (!roles?.includes("buyer")) {
      console.log("❌ Showing modal because no buyer role");
      setShowConfirmModal(true);
      return;
    }
  };

  return (
    <>
      {isSwitching && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Loader />
        </Box>
      )}
      <Seo
        title="Buy Scrap Online | Trusted Scrap Marketplace"
        description="Shop scrap metal from verified sellers. Get fair market pricing, safe payments, and seamless logistics when you buy scrap online with ScrapTrade."
        url="https://scraptrade.com.au/buy"
        canonical="https://scraptrade.com.au/buy"
        image="https://scraptrade.com.au/assets/og/scrap.jpg"
        breadcrumbs={breadcrumbs}
        extraSchema={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Scrap Materials",
          description:
            "Buy high-quality scrap materials from verified suppliers across the globe through ScrapTrade.",
          image: "https://scraptrade.com.au/assets/og/scrap.jpg",
          brand: {
            "@type": "Organization",
            name: "ScrapTrade",
            logo: "https://scraptrade.com.au/assets/scrap.png",
          },
          offers: {
            "@type": "AggregateOffer",
            availability: "https://schema.org/InStock",
            priceCurrency: "USD",
            lowPrice: "100",
            highPrice: "10000",
            url: "https://scraptrade.com.au/buy",
          },
        }}
      />
      <Box
        sx={{
          fontFamily: "Roboto, sans-serif",
          backgroundColor: themeColors.background,
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            py: 12,
            textAlign: "center",
            background: `linear-gradient(145deg, ${themeColors.background}, #e2e8f0)`,
          }}
        >
          <Container>
            <motion.div
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{ color: themeColors.secondary }}
              >
                Buy Scrap Online with ScrapTrade
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                Get the scrap you need from verified sellers with competitive
                pricing, and secure transactions.
              </Typography>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Button
                  // component={Link}
                  // to={isBuyerLoggedIn ? "/dashboard" : "/register?role=buyer"}

                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: themeColors.primary,
                    fontWeight: "bold",
                    color: "#fff",
                    boxShadow: `0 0 10px ${themeColors.primary}`,
                    px: 4,
                    "&:hover": {
                      backgroundColor: "#d49f00",
                    },
                  }}
                  onClick={handleBuyerRoleSwitch}
                >
                  {isBuyerLoggedIn ? "Go to Dashboard" : "Join as Buyer"}
                </Button>
              </motion.div>
            </motion.div>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ mt: 3, mb: 1 }}>
          <Breadcrumb items={breadcrumbs} />
        </Container>
        <KycBanner />

        {/* Benefits Section */}
        <Box sx={{ py: 10 }}>
          <Container>
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ color: themeColors.secondary }}
            >
              Why Buy Scrap on ScrapTrade?
            </Typography>
            <Grid container spacing={4} justifyContent="center" sx={{ mt: 4 }}>
              {benefits.map((item, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <motion.div
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={idx}
                  >
                    <Paper
                      sx={{
                        width: 250,
                        height: 240,
                        p: 3,
                        borderRadius: 3,
                        position: "relative",
                        backgroundColor: themeColors.cardBackground,
                        boxShadow: `0 0 15px ${themeColors.shadow}`,
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-10px)",
                          boxShadow: `0 0 25px ${themeColors.primary}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: -20,
                          left: -20,
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: themeColors.primary,
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          fontSize: "1.1rem",
                          boxShadow: `0 0 10px ${themeColors.primary}`,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Box sx={{ mb: 2 }}>{item.icon}</Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {item.desc}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Buyer Journey Section */}
        <Box
          sx={{
            py: 10,
            background: `linear-gradient(to bottom, #ffffff, ${themeColors.background})`,
          }}
        >
          <Container>
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ color: themeColors.secondary }}
            >
              How to Start Buying Scrap
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }} justifyContent="center">
              {[
                {
                  step: "Register & Browse",
                  detail:
                    "Sign up, verify your account, and explore listings with smart filters.",
                },
                {
                  step: "Place Offers",
                  detail:
                    "Submit bids or RFQs, negotiate with sellers, and confirm your deal.",
                },
                {
                  step: "Secure Payment",
                  detail:
                    "Pay safely through escrow for guaranteed and transparent transactions.",
                },
                {
                  step: "Delivery & Receive",
                  detail:
                    "Arrange pickup or delivery, track in real time, and confirm receipt.",
                },
              ].map((item, idx) => {
                const Icon = journeyIcons[idx];
                return (
                  <Grid item xs={6} sm={3} key={idx}>
                    <motion.div
                      variants={fadeInUp}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      custom={idx}
                    >
                      <Paper
                        sx={{
                          aspectRatio: "1",
                          height: "250px",
                          p: 1.5,
                          borderRadius: 2,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          backgroundColor: themeColors.cardBackground,
                          boxShadow: `0 0 8px ${themeColors.shadow}`,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: `0 0 12px ${themeColors.primary}`,
                          },
                        }}
                      >
                        <Box sx={{ mb: 1, color: themeColors.primary }}>
                          <Icon size={60} />
                        </Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight="600"
                          fontSize="0.9rem"
                        >
                          {idx + 1}. {item.step}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize="0.75rem"
                          sx={{ mt: 0.5 }}
                        >
                          {item.detail}
                        </Typography>
                      </Paper>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        {/* Stats Section */}
        {/* <Box sx={{ py: 10 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          textAlign="center"
          gutterBottom
          sx={{ color: themeColors.secondary }}
        >
          ScrapTrade by the Numbers
        </Typography>
        <Container>
          <Grid container spacing={4} justifyContent="center">
            {[
              { label: "Verified Sellers", value: "1,200+" },
              { label: "Tonnes Traded", value: "10,000+" },
              { label: "States Covered", value: "All 6+" },
              { label: "Buyer Satisfaction", value: "98%" },
            ].map((item, idx) => (
              <Grid item xs={6} md={3} key={idx}>
                <motion.div
                  variants={fadeInUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={idx}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{ color: themeColors.primary }}
                    >
                      {item.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

        {/* Stats Section */}
        <Box
          sx={{
            py: 12,
            background: `linear-gradient(180deg, ${alpha(
              themeColors.primary,
              0.04,
            )}, ${alpha(themeColors.background, 1)})`,
          }}
        >
          <Container>
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ color: themeColors.secondary, mb: 6 }}
            >
              ScrapTrade by the Numbers
            </Typography>

            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  label: "Verified Sellers",
                  value: 1200,
                  suffix: "+",
                  icon: Factory,
                },
                {
                  label: "Tonnes Traded",
                  value: 10000,
                  suffix: "+",
                  icon: TrendingUp,
                },
                {
                  label: "States Covered",
                  value: 6,
                  prefix: "All ",
                  suffix: "+",
                  icon: Globe,
                },
                {
                  label: "Buyer Satisfaction",
                  value: 98,
                  suffix: "%",
                  icon: Star,
                },
              ].map((item, idx) => (
                <Grid item xs={6} md={3} key={idx}>
                  <motion.div
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={idx}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 5,
                        borderRadius: 4,
                        textAlign: "center",
                        border: `1px solid ${alpha(themeColors.primary, 0.1)}`,
                        background: alpha(themeColors.primary, 0.02),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px) scale(1.02)",
                          boxShadow: `0 12px 30px ${alpha(
                            themeColors.primary,
                            0.2,
                          )}`,
                          background: alpha(themeColors.primary, 0.05),
                        },
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: alpha(themeColors.primary, 0.1),
                          mx: "auto",
                          mb: 3,
                        }}
                      >
                        <item.icon size={32} color={themeColors.primary} />
                      </Box>

                      {/* Animated Number */}
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ color: themeColors.primary }}
                      >
                        <CountUp
                          start={0}
                          end={item.value}
                          duration={2.5}
                          separator=","
                          prefix={item.prefix || ""}
                          suffix={item.suffix || ""}
                        />
                      </Typography>

                      {/* Label */}
                      <Typography
                        variant="body1"
                        sx={{
                          mt: 1,
                          color: alpha(themeColors.secondary, 0.9),
                          fontWeight: 500,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Random Product Section with productsReady callback */}
        <RandomProductHomeSection
          onProductsLoaded={() => setProductsReady(true)}
        />

        {/* Benefits of Using ScrapTrade for Buying Scrap */}
        <Box sx={{ py: 10 }}>
          <Container>
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ color: themeColors.secondary }}
            >
              Benefits of Buying on ScrapTrade
            </Typography>
            <Grid container spacing={4} justifyContent="center" sx={{ mt: 4 }}>
              {extraBenefits.map((item, idx) => (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <motion.div
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={idx}
                  >
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        textAlign: "center",
                        backgroundColor: themeColors.cardBackground,
                        boxShadow: `0 0 15px ${themeColors.shadow}`,
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: `0 0 25px ${themeColors.primary}`,
                        },
                      }}
                    >
                      <Box sx={{ mb: 2 }}>{item.icon}</Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {item.desc}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            py: 10,
            backgroundColor: themeColors.ctaBackground,
            textAlign: "center",
          }}
        >
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                sx={{ color: "#fff" }}
              >
                Ready to buy Scrap Smarter?
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: "#e0e0e0" }}>
                Join ScrapTrade today and connect with verified sellers, lock in
                the best prices, and buy with complete security.
              </Typography>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Button
                  component={Link}
                  to={isBuyerLoggedIn ? "/dashboard" : "/register?role=buyer"}
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: themeColors.primary,
                    fontWeight: "bold",
                    px: 4,
                    color: "#fff",
                    boxShadow: `0 0 10px ${themeColors.primary}`,
                    "&:hover": {
                      backgroundColor: "#d49f00",
                    },
                  }}
                  onClick={handleBuyerRoleSwitch}
                >
                  {isBuyerLoggedIn
                    ? "Go to Buyer Dashboard"
                    : "Start Buying Now"}
                </Button>
              </motion.div>
            </motion.div>
          </Container>
        </Box>
      </Box>

      <VerifySwitchRoleModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Switch to Buyer Role"
        description="You are about to activate your seller account."
        confirmText="Activate"
        cancelText="Cancel"
        onConfirm={handleConfirmRoleSwitch}
      />
    </>
  );
};

export default BuyerLandingPage;
